import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
/**
 * For consistency. Utility Function to extract value from an event.
 * @param e Change Event on an input element.
 * @returns Parsed float value of the new input value or NaN if empty
 */
function getValueFromOnChangeEvent(e) {
    const parsedValue = parseFloat(e?.target?.value);
    if (Number.isNaN(parsedValue)) {
        return '';
    }
    return parsedValue;
}
export default function FilterRangeSlider(props) {
    const { rangedFacet: facet, modifyRequestRangeFilter, isCollapsed, sliderStep = 0.1 } = props;
    const visibleTrack = useRef(null);
    const [selectedTrackStyles, setSelectedTrackStyles] = useState({});
    const [minValue, setMinValue] = useState(facet.min);
    const [maxValue, setMaxValue] = useState(facet.max);
    const [inputMinValue, setInputMinValue] = useState(facet.status?.min || facet.min);
    const [inputMaxValue, setInputMaxValue] = useState(facet.status?.max || facet.max);
    const [filterRange, setFilterRange] = useState('');
    const [isModified, setIsModified] = useState(false);
    const isValidMinValue = (value) => typeof value !== 'string' && value < maxValue && value >= facet.min;
    const isValidMaxValue = (value) => typeof value !== 'string' && value > minValue && value <= facet.max;
    const onMinSliderMove = (event) => {
        const sliderValue = getValueFromOnChangeEvent(event);
        if (isValidMinValue(sliderValue)) {
            setMinValue(sliderValue);
            setInputMinValue(sliderValue);
            setIsModified(true);
        }
    };
    const onMaxSliderMove = (event) => {
        const sliderValue = getValueFromOnChangeEvent(event);
        if (isValidMaxValue(sliderValue)) {
            setMaxValue(sliderValue);
            setInputMaxValue(sliderValue);
            setIsModified(true);
        }
    };
    const onSliderMoveEnd = () => {
        const newRange = `${minValue}-${maxValue}`;
        modifyRequestRangeFilter(newRange);
    };
    const onMaxInputValueChange = (event) => {
        const inputValue = getValueFromOnChangeEvent(event);
        setInputMaxValue(inputValue);
        if (isValidMaxValue(inputValue)) {
            setMaxValue(inputValue);
            setIsModified(true);
        }
    };
    const onMinInputValueChange = (event) => {
        const inputValue = getValueFromOnChangeEvent(event);
        setInputMinValue(inputValue);
        if (isValidMinValue(inputValue)) {
            setMinValue(inputValue);
            setIsModified(true);
        }
    };
    const onInputBlurApplyFilter = () => {
        if (isModified && isValidMaxValue(inputMaxValue) && isValidMinValue(inputMinValue)) {
            modifyRequestRangeFilter(filterRange);
        }
    };
    const onTrackClick = (event) => {
        if (visibleTrack.current === null)
            return;
        if (event.target !== visibleTrack.current)
            return;
        const totalWidth = visibleTrack.current.offsetWidth;
        const clickedX = event.nativeEvent.offsetX;
        const selectedValue = Math.round((clickedX / totalWidth) * (facet.max - facet.min)) + facet.min;
        const distMinToClicked = Math.abs(selectedValue - minValue);
        const distMaxToClicked = Math.abs(selectedValue - maxValue);
        if (distMinToClicked <= distMaxToClicked) {
            const newRange = `${selectedValue}-${maxValue}`;
            setMinValue(selectedValue);
            setInputMinValue(selectedValue);
            modifyRequestRangeFilter(newRange);
        }
        else {
            const newRange = `${minValue}-${selectedValue}`;
            setMaxValue(selectedValue);
            setInputMaxValue(selectedValue);
            modifyRequestRangeFilter(newRange);
        }
        setIsModified(true);
    };
    // Update internal state
    useEffect(() => {
        if (facet.status.min !== undefined && !isModified) {
            // Initial state
            setMinValue(facet.status.min);
            setMaxValue(facet.status.max);
            setFilterRange(`${facet.status.min}-${facet.status.max}`);
        }
        else if (isModified) {
            // Future updates
            setFilterRange(`${minValue}-${maxValue}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minValue, maxValue, facet]);
    // Update selected track styles
    useEffect(() => {
        const trackLen = facet.max - facet.min;
        const rebasedStartValue = minValue - facet.min;
        const startPercentage = ((100 * rebasedStartValue) / trackLen).toFixed(2);
        const widthPercentage = ((100 * (maxValue - minValue)) / trackLen).toFixed(2);
        setSelectedTrackStyles({ left: `${startPercentage}%`, width: `${widthPercentage}%` });
    }, [minValue, maxValue, facet]);
    return (React.createElement("div", { className: classNames({
            'cio-collapsible-wrapper': true,
            'cio-collapsible-is-open': !isCollapsed,
        }) },
        React.createElement("div", { className: 'cio-collapsible-inner' },
            React.createElement("div", { className: 'cio-filter-ranged-slider' },
                React.createElement("div", { className: 'cio-slider-inputs' },
                    React.createElement("span", { className: 'cio-slider-input cio-slider-input-min' },
                        React.createElement("span", { className: 'cio-slider-input-prefix' }, "from "),
                        React.createElement("input", { required: true, type: 'number', value: inputMinValue, onChange: onMinInputValueChange, onBlur: onInputBlurApplyFilter, placeholder: facet.min.toString(), min: facet.min, max: maxValue, step: sliderStep })),
                    React.createElement("div", { className: 'cio-slider-input cio-slider-input-max' },
                        React.createElement("span", { className: 'cio-slider-input-prefix' }, "to "),
                        React.createElement("input", { required: true, type: 'number', value: inputMaxValue, onChange: onMaxInputValueChange, onBlur: onInputBlurApplyFilter, placeholder: facet.max.toString(), min: minValue, max: facet.max, step: sliderStep }))),
                React.createElement("div", { className: 'cio-doubly-ended-slider', ref: visibleTrack, role: 'presentation', onClick: (e) => onTrackClick(e) },
                    React.createElement("div", { className: 'cio-slider-track-selected', style: selectedTrackStyles }),
                    React.createElement("input", { className: 'cio-min-slider', type: 'range', step: sliderStep, min: facet.min, max: facet.max, value: minValue, onChange: onMinSliderMove, onMouseUp: onSliderMoveEnd, onTouchEnd: onSliderMoveEnd }),
                    React.createElement("input", { className: 'cio-max-slider', type: 'range', step: sliderStep, min: facet.min, max: facet.max, value: maxValue, onChange: onMaxSliderMove, onMouseUp: onSliderMoveEnd, onTouchEnd: onSliderMoveEnd }))))));
}
